import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, Stack, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper, 
  TablePagination, Divider, Chip, MenuItem, CircularProgress
} from "@mui/material";
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon,
  Delete as DeleteIcon, Room as RoomIcon
} from "@mui/icons-material";
import { db } from "../../firebase"; 
import { 
  collection, addDoc, getDocs, query, doc, 
  updateDoc, deleteDoc, Timestamp, where 
} from "firebase/firestore";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";

function Hostelrooms() {
  const [rooms, setRooms] = useState([]);
  const [hostels, setHostels] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  
  const [search, setSearch] = useState("");
  const [filterHostel, setFilterHostel] = useState("All"); // New state for hostel filter
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const currentSchoolId = "zIlbJM5BjyrlEhg7fWKs";

  const [form, setForm] = useState({
    hostelId: "",
    roomNo: "",
    noOfBeds: "",
    maxStudents: "",
    status: "Available",
    schoolId: currentSchoolId
  });

  useEffect(() => {
    fetchHostels();
    fetchRooms();
  }, []);

  const fetchHostels = async () => {
    try {
      const q = query(collection(db, "hostels"), where("schoolId", "==", currentSchoolId));
      const snap = await getDocs(q);
      setHostels(snap.docs.map(doc => ({ id: doc.id, name: doc.data().hostelName })));
    } catch (err) { console.error(err); }
  };

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "hostelrooms"), 
        where("schoolId", "==", currentSchoolId),
      );
      const snap = await getDocs(q);
      setRooms(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.hostelId || !form.roomNo || !form.noOfBeds) {
      return Swal.fire("Error", "Please fill all required fields", "error");
    }
    setLoading(true);
    try {
      const payload = { ...form, updatedAt: Timestamp.now() };
      if (editId) {
        await updateDoc(doc(db, "hostelrooms", editId), payload);
        Swal.fire("Updated", "Room updated successfully", "success");
      } else {
        await addDoc(collection(db, "hostelrooms"), payload);
        Swal.fire("Saved", "Room added successfully", "success");
      }
      setOpen(false);
      resetForm();
      fetchRooms();
    } catch (err) { Swal.fire("Error", err.message, "error"); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Room?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#64748B"
    });
    if (confirm.isConfirmed) {
      try {
        await deleteDoc(doc(db, "hostelrooms", id));
        Swal.fire("Deleted", "Room has been removed.", "success");
        fetchRooms();
      } catch (err) { Swal.fire("Error", err.message, "error"); }
    }
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      hostelId: "", roomNo: "", noOfBeds: "",
      maxStudents: "", status: "Available", schoolId: currentSchoolId
    });
  };

  // Filter data by both Search Query (Room No) AND Selected Hostel
  const filteredData = rooms.filter(r => {
    const matchSearch = r.roomNo ? String(r.roomNo).toLowerCase().includes(search.toLowerCase()) : false;
    const matchHostel = filterHostel === "All" ? true : r.hostelId === filterHostel;
    return matchSearch && matchHostel;
  });

  return (
    <Box p={2} sx={{ background: "#f8fafc", minHeight: "100vh" }}>
      {/* HEADER SECTION */}
      <Stack direction="row" justifyContent="space-between" mb={3} alignItems="center">
        <Box>
          <Typography variant="h6" fontWeight="bold">Hostel Room Management</Typography>
          <Typography variant="caption" color="text.secondary">Assign beds and manage room availability</Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<AddIcon />} sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: "#0f766e" } }} onClick={() => { resetForm(); setOpen(true); }}>
          Add Room
        </Button>
      </Stack>

      {/* SEARCH, FILTER, AND TABLE DATA CARD */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #e2e8f0" }} elevation={0}>
        
        {/* Search & Filter Bar */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} p={1.5} sx={{ bgcolor: "#fff" }}>
          <TextField 
            fullWidth size="small" placeholder="Search by Room Number..." 
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "gray", fontSize: 18 }} /> }}
          />
          <TextField 
            select 
            size="small" 
            value={filterHostel} 
            onChange={(e) => { setFilterHostel(e.target.value); setPage(0); }}
            sx={{ minWidth: { xs: '100%', sm: 250 } }}
          >
            <MenuItem value="All">All Hostels</MenuItem>
            {hostels.map(h => (
              <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>
            ))}
          </TextField>
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f1f5f9" }}>
              <TableRow>
                <TableCell><b>Sr.</b></TableCell>
                <TableCell><b>Hostel</b></TableCell>
                <TableCell><b>Room No.</b></TableCell>
                <TableCell><b>Beds / Capacity</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell align="center"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}><CircularProgress size={24} sx={{ color: PRIMARY }} /></TableCell></TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.disabled" }}>No records found</TableCell></TableRow>
              ) : (
                filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{hostels.find(h => h.id === row.hostelId)?.name || "Unknown"}</TableCell>
                    <TableCell><b>Room {row.roomNo}</b></TableCell>
                    <TableCell>{row.noOfBeds} Beds / {row.maxStudents || 0} Max Capacity</TableCell>
                    <TableCell>
                      <Chip label={row.status} size="small" variant="outlined" color={row.status === "Available" ? "success" : row.status === "Full" ? "error" : "warning"} />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => { setEditId(row.id); setForm(row); setOpen(true); }} color="primary"><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleDelete(row.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination 
          component="div" 
          count={filteredData.length} 
          rowsPerPage={rowsPerPage} 
          page={page} 
          onPageChange={(e, n) => setPage(n)} 
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      {/* COMPACT CLEAN ROOM FORM DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, fontSize: '1rem', fontWeight: 'bold' }}>
          <RoomIcon sx={{ color: PRIMARY }} /> {editId ? "Update Room Settings" : "Add New Room"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Select Hostel" fullWidth size="small" value={form.hostelId} onChange={(e) => setForm({...form, hostelId: e.target.value})}>
              {hostels.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
            </TextField>
            
            <TextField label="Room Number" fullWidth size="small" value={form.roomNo} onChange={(e) => setForm({...form, roomNo: e.target.value})} />
            
            <Stack direction="row" spacing={2}>
              <TextField label="No. of Beds" type="number" fullWidth size="small" value={form.noOfBeds} onChange={(e) => setForm({...form, noOfBeds: e.target.value})} />
              <TextField label="Max Students" type="number" fullWidth size="small" value={form.maxStudents} onChange={(e) => setForm({...form, maxStudents: e.target.value})} />
            </Stack>

            <TextField select label="Status" fullWidth size="small" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
              <MenuItem value="Available">Available</MenuItem>
              <MenuItem value="Full">Full</MenuItem>
              <MenuItem value="Under Maintenance">Under Maintenance</MenuItem>
            </TextField>
          </Stack>
          
          <Button variant="contained" fullWidth sx={{ mt: 3, bgcolor: PRIMARY, '&:hover': { bgcolor: "#0f766e" }, textTransform: 'none' }} onClick={handleSubmit} disabled={loading}>
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : editId ? "Update Changes" : "Save Room Profile"}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Hostelrooms;